import React, {
  forwardRef,
  memo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import 'react-dropdown-tree-select/dist/styles.css';
import { StyledFormHelper, StyledUiTreeSelect } from './UiTreeSelect.styles';

const UiTreeSelect = forwardRef(
  (
    {
      name,
      control,
      data = [],
      children,
      searchPlaceholder,
      className = '',
      defaultValue,
      noMatches,
      ...rest
    },
    ref
  ) => {
    const [tree, setTree] = useState(data);

    const getCheckedTree = useCallback((tree, checkedId) => {
      return tree.map((node) => {
        if (node.value === checkedId) {
          return { ...node, checked: true };
        }
        if (node.children) {
          return {
            ...{ ...node, checked: false },
            children: getCheckedTree(node.children, checkedId),
          };
        }
        return { ...node, checked: false };
      });
    }, []);

    useEffect(() => {
      if (control) {
        setTree(getCheckedTree(data, defaultValue));
      }
    }, [control, data, defaultValue, getCheckedTree, name]);

    if (control)
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <StyledUiTreeSelect
                  data={tree}
                  mode='radioSelect'
                  inlineSearchInput={true}
                  key={value}
                  texts={{
                    placeholder: ' ',
                    inlineSearchPlaceholder: searchPlaceholder,
                    noMatches: noMatches,
                  }}
                  {...rest}
                  ref={ref}
                  className={classNames('ui-tree-select', error && 'error')}
                  onChange={(currentNode) => {
                    setTree((prevTree) =>
                      getCheckedTree(prevTree, currentNode.value)
                    );
                    onChange(currentNode || '');
                  }}
                />
                {error && <StyledFormHelper>{error.message}</StyledFormHelper>}
              </>
            );
          }}
        />
      );

    return (
      <StyledUiTreeSelect
        name={name}
        ref={ref}
        className={className}
        data={data}
        mode='radioSelect'
        inlineSearchInput={true}
        texts={{
          placeholder: ' ',
          inlineSearchPlaceholder: searchPlaceholder,
        }}
        {...rest}
      />
    );
  }
);

UiTreeSelect.propTypes = {
  className: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
      children: PropTypes.array,
    })
  ),
  defaultValue: PropTypes.string,
  children: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  control: PropTypes.object,
  noMatches: PropTypes.string,
};

export default memo(UiTreeSelect);
